import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, shareReplay, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PermissionListResponse, Permiso } from '../models/api.models';

/**
 * Service for managing permissions with caching
 * Permissions are loaded once and cached until logout
 */
@Injectable({
    providedIn: 'root'
})
export class PermissionsService {
    private readonly http = inject(HttpClient);
    private readonly API_BASE_URL = environment.apiBaseUrl;
    private readonly PERMISSIONS_ENDPOINT = '/admin/permissions';

    // Cache for permissions
    private permissionsCache$: Observable<PermissionListResponse> | null = null;
    private permissionsCacheSubject = new BehaviorSubject<Permiso[]>([]);
    public readonly permissions$ = this.permissionsCacheSubject.asObservable();

    /**
     * Get all available permissions (cached)
     * First call fetches from API, subsequent calls return cached data
     */
    getPermissions(): Observable<PermissionListResponse> {
        // If cache exists, return it
        if (this.permissionsCache$) {
            return this.permissionsCache$;
        }

        // Otherwise, fetch from API and cache the result
        const url = `${this.API_BASE_URL}${this.PERMISSIONS_ENDPOINT}`;
        this.permissionsCache$ = this.http.get<PermissionListResponse>(url).pipe(
            tap(response => {
                // Update the BehaviorSubject with the permissions
                this.permissionsCacheSubject.next(response.permisos);
                console.log('Permissions loaded and cached:', response.permisos.length, 'permissions');
            }),
            shareReplay(1), // Share the result with all subscribers
            catchError(error => {
                console.error('Error loading permissions:', error);
                // Clear cache on error so it can be retried
                this.permissionsCache$ = null;
                throw error;
            })
        );

        return this.permissionsCache$;
    }

    /**
     * Get cached permissions synchronously
     * Returns empty array if not loaded yet
     */
    getCachedPermissions(): Permiso[] {
        return this.permissionsCacheSubject.value;
    }

    /**
     * Check if permissions are loaded
     */
    isLoaded(): boolean {
        return this.permissionsCacheSubject.value.length > 0;
    }

    /**
     * Clear the permissions cache
     * Should be called on logout
     */
    clearCache(): void {
        this.permissionsCache$ = null;
        this.permissionsCacheSubject.next([]);
        console.log('Permissions cache cleared');
    }

    /**
     * Preload permissions (useful to call after login)
     */
    preloadPermissions(): void {
        if (!this.isLoaded()) {
            this.getPermissions().subscribe({
                next: () => console.log('Permissions preloaded successfully'),
                error: (error) => console.error('Failed to preload permissions:', error)
            });
        }
    }
}
